import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import LaunchExperience from "./components/LaunchExperience";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSkeleton from "./components/LoadingSkeleton";

// Eager (needed on every navigation)
import Login from "./pages/Login";
import RequestDispatchServices from "./pages/public/RequestDispatchServices";
import AdminShell from "./pages/admin/AdminShell";
import DriverShell from "./pages/driver/DriverShell";
import CustomerShell from "./pages/customer/CustomerShell";

// Lazy — everything else. Cuts initial bundle by ~55 chunks. Each chunk
// is fetched on demand via React.lazy() the first time its route is
// navigated to.
const CommandCenter = lazy(() => import("./pages/admin/CommandCenter"));
const LoadBoard = lazy(() => import("./pages/admin/LoadBoard"));
const LoadDetail = lazy(() => import("./pages/admin/LoadDetail"));
const LiveMap = lazy(() => import("./pages/admin/LiveMap"));
const AIControl = lazy(() => import("./pages/admin/AIControl"));
const EventsLog = lazy(() => import("./pages/admin/EventsLog"));
const Invoices = lazy(() => import("./pages/admin/Invoices"));
const DriversPage = lazy(() => import("./pages/admin/DriversPage"));
const DriverForm = lazy(() => import("./pages/admin/DriverForm"));
const DriverDetail = lazy(() => import("./pages/admin/DriverDetail"));
const LoadBoardHub = lazy(() => import("./pages/admin/LoadBoardHub"));
const AILoadBooking = lazy(() => import("./pages/admin/AILoadBooking"));
const RecruitingPipeline = lazy(() => import("./pages/admin/RecruitingPipeline"));
const RecruitingOps = lazy(() => import("./pages/admin/RecruitingOps"));
const VoiceAssignments = lazy(() => import("./pages/admin/VoiceAssignments"));
const FollowupSequences = lazy(() => import("./pages/admin/FollowupSequences"));
const CarrierProfile = lazy(() => import("./pages/admin/CarrierProfile"));
const RecruitingAnalytics = lazy(() => import("./pages/admin/RecruitingAnalytics"));
const ScoringWeights = lazy(() => import("./pages/admin/ScoringWeights"));
const BrokersPage = lazy(() => import("./pages/admin/BrokersPage"));
const SettlementsPage = lazy(() => import("./pages/admin/SettlementsPage"));
const PaymentPreferencesAdmin = lazy(() => import("./pages/admin/PaymentPreferences"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const AutonomousOps = lazy(() => import("./pages/admin/AutonomousOps"));
const DataManagement = lazy(() => import("./pages/admin/DataManagement"));
const Integrations = lazy(() => import("./pages/admin/Integrations"));
const IntegrationCenter = lazy(() => import("./pages/admin/IntegrationCenter"));
const IntegrationsHub = lazy(() => import("./pages/admin/IntegrationsHub"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const GoLiveReadiness = lazy(() => import("./pages/admin/GoLiveReadiness"));
const Applications = lazy(() => import("./pages/admin/Applications"));
const Carriers = lazy(() => import("./pages/admin/Carriers"));
const Referrals = lazy(() => import("./pages/admin/Referrals"));
const RecruitingWorkspace = lazy(() => import("./pages/admin/RecruitingWorkspace"));
const OpsKPIs = lazy(() => import("./pages/admin/OpsKPIs"));
const Dispatchers = lazy(() => import("./pages/admin/Dispatchers"));
const Profitability = lazy(() => import("./pages/admin/Profitability"));
const Compliance = lazy(() => import("./pages/admin/Compliance"));
const AuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const ActivityTimeline = lazy(() => import("./pages/admin/ActivityTimeline"));
const FollowUps = lazy(() => import("./pages/admin/FollowUps"));
const Payroll = lazy(() => import("./pages/admin/Payroll"));
const CheckCalls = lazy(() => import("./pages/admin/CheckCalls"));
const Ifta = lazy(() => import("./pages/admin/Ifta"));
const Factoring = lazy(() => import("./pages/admin/Factoring"));
const Pricing = lazy(() => import("./pages/admin/Pricing"));
const Edi = lazy(() => import("./pages/admin/Edi"));
const Branding = lazy(() => import("./pages/admin/Branding"));
const OnboardingConfig = lazy(() => import("./pages/admin/OnboardingConfig"));
const BriefsTimeline = lazy(() => import("./pages/admin/BriefsTimeline"));
const AdminNotFound = lazy(() => import("./pages/admin/AdminNotFound"));
const VoiceLive = lazy(() => import("./pages/admin/voice/VoiceLive"));
const VoiceHealth = lazy(() => import("./pages/admin/voice/VoiceHealth"));
const VoicePersonas = lazy(() => import("./pages/admin/voice/VoicePersonas"));
const VoiceHistory = lazy(() => import("./pages/admin/voice/VoiceHistory"));
const VoiceTransferTargets = lazy(() => import("./pages/admin/voice/VoiceTransferTargets"));
const VoiceActions = lazy(() => import("./pages/admin/voice/VoiceActions"));
const VoiceConfig = lazy(() => import("./pages/admin/voice/VoiceConfig"));
const VoiceMonitor = lazy(() => import("./pages/admin/voice/VoiceMonitor"));
const VoiceAnalytics = lazy(() => import("./pages/admin/voice/VoiceAnalytics"));
const VoiceCarrierApplications = lazy(() => import("./pages/admin/voice/VoiceCarrierApplications"));
const ReceptionistAnalytics = lazy(() => import("./pages/admin/voice/ReceptionistAnalytics"));
const TTSSettings = lazy(() => import("./pages/admin/voice/TTSSettings"));
const VoiceEngine = lazy(() => import("./pages/admin/VoiceEngine"));
const Leads = lazy(() => import("./pages/admin/Leads"));
const Knowledge = lazy(() => import("./pages/admin/Knowledge"));
const Recruiting = lazy(() => import("./pages/admin/Recruiting"));
const RecruiterAnalytics = lazy(() => import("./pages/admin/RecruiterAnalytics"));
const FollowUpAgent = lazy(() => import("./pages/admin/FollowUpAgent"));
const ProspectTimeline = lazy(() => import("./pages/admin/ProspectTimeline"));
const CarrierDiscovery = lazy(() => import("./pages/admin/CarrierDiscovery"));
const CarrierPipeline = lazy(() => import("./pages/admin/CarrierPipeline"));
const CarrierFollowUpQueue = lazy(() => import("./pages/admin/CarrierFollowUpQueue"));
const Security = lazy(() => import("./pages/Security"));
const Vehicles = lazy(() => import("./pages/admin/Vehicles"));
// iter-139 · Carrier-centric Operations (unified application review + per-carrier profile)
const UnifiedApplicationReview   = lazy(() => import("./pages/admin/operations/UnifiedApplicationReview"));
const CarrierOperationalProfile  = lazy(() => import("./pages/admin/operations/CarrierOperationalProfile"));

// Phase 2 · v3.1 — shared hub layout that renders a horizontal tab
// strip and delegates page rendering to <Outlet />. Every top-level
// sidebar destination is now a TabbedHub; each tab is a real route
// so URLs stay bookmarkable and browser back/forward works.
import TabbedHub from "./components/admin/TabbedHub";

// ── Hub tab configurations ────────────────────────────────────────
// Kept adjacent to the route registrations below so it's obvious
// which page each tab renders.  `to` values are ABSOLUTE so tab
// clicks always route to the correct nested path regardless of the
// currently-active tab.
const OPS_DISPATCH_TABS = [
  { key: "loads",      label: "Loads",           to: "/admin/operations/dispatch/loads" },
  { key: "sourcing",   label: "Load Sourcing",   to: "/admin/operations/dispatch/sourcing" },
  { key: "ai-booking", label: "AI Load Booking", to: "/admin/operations/dispatch/ai-booking" },
  { key: "map",        label: "Live Map",        to: "/admin/operations/dispatch/map" },
  { key: "checkcalls", label: "Check Calls",     to: "/admin/operations/dispatch/checkcalls" },
];
const OPS_DRIVERS_TABS = [
  { key: "drivers",     label: "Drivers",     to: "/admin/operations/drivers/drivers" },
  { key: "dispatchers", label: "Dispatchers", to: "/admin/operations/drivers/dispatchers" },
];
const OPS_VEHICLES_TABS = [
  { key: "vehicles",  label: "Vehicles",  to: "/admin/operations/vehicles/vehicles" },
  { key: "carriers",  label: "Carriers",  to: "/admin/operations/vehicles/carriers" },
  { key: "brokers",   label: "Brokers",   to: "/admin/operations/vehicles/brokers" },
];
// iter-139 · Carrier-centric Operations hub (replaces the legacy
// Vehicles hub as the carrier/application home).
const OPS_CARRIERS_TABS = [
  { key: "directory",    label: "Carrier Directory",  to: "/admin/operations/carriers/directory" },
  { key: "applications", label: "Application Review", to: "/admin/operations/carriers/applications" },
  { key: "brokers",      label: "Brokers",            to: "/admin/operations/carriers/brokers" },
  { key: "referrals",    label: "Referrals",          to: "/admin/operations/carriers/referrals" },
];
const OPS_COMPLIANCE_TABS = [
  { key: "overview",   label: "Compliance",        to: "/admin/operations/compliance/overview" },
  { key: "ifta",       label: "IFTA",              to: "/admin/operations/compliance/ifta" },
  { key: "audit",      label: "Audit Log",         to: "/admin/operations/compliance/audit" },
  { key: "security",   label: "Security · 2FA",    to: "/admin/operations/compliance/security" },
  { key: "onboarding", label: "Carrier Onboarding",to: "/admin/operations/compliance/onboarding" },
];
const REC_PIPELINE_TABS = [
  { key: "pipeline",  label: "Pipeline",           to: "/admin/recruiting/pipeline/pipeline" },
  { key: "discovery", label: "Carrier Discovery",  to: "/admin/recruiting/pipeline/discovery" },
  { key: "journey",   label: "Prospect Journey",   to: "/admin/recruiting/pipeline/journey" },
];
const REC_CAMPAIGNS_TABS = [
  { key: "sequences",  label: "Outreach Sequences", to: "/admin/recruiting/campaigns/sequences" },
  { key: "needs-info", label: "Needs Info Queue",   to: "/admin/recruiting/campaigns/needs-info" },
  { key: "settings",   label: "Recruiter Settings", to: "/admin/recruiting/campaigns/settings" },
];
const REC_APPLICANTS_TABS = [
  { key: "applications",       label: "Applications",       to: "/admin/recruiting/applicants/applications" },
  { key: "voice-applications", label: "Voice Applications", to: "/admin/recruiting/applicants/voice-applications" },
  { key: "leads",              label: "Leads",              to: "/admin/recruiting/applicants/leads" },
];
const REC_ANALYTICS_TABS = [
  { key: "overview",  label: "Overview",       to: "/admin/recruiting/analytics/overview" },
  { key: "recruiter", label: "Recruiter KPIs", to: "/admin/recruiting/analytics/recruiter" },
  { key: "follow-up", label: "Follow-Up KPIs", to: "/admin/recruiting/analytics/follow-up" },
  { key: "scoring",   label: "AI Scoring",     to: "/admin/recruiting/analytics/scoring" },
];
const AI_AGENTS_TABS = [
  { key: "control",    label: "AI Control",              to: "/admin/ai/agents/control" },
  { key: "autonomous", label: "AI Autopilot",            to: "/admin/ai/agents/autonomous" },
  { key: "personas",   label: "Personas",                to: "/admin/ai/agents/personas" },
  { key: "assignments",label: "Voice Assignments",       to: "/admin/ai/configuration/assignments" },
  { key: "reminders",  label: "Operational Reminders",   to: "/admin/ai/agents/reminders" },
];
const AI_CALLS_TABS = [
  { key: "live",      label: "Live Calls",       to: "/admin/ai/calls/live" },
  { key: "history",   label: "Call History",     to: "/admin/ai/calls/history" },
  { key: "actions",   label: "Voice Actions",    to: "/admin/ai/calls/actions" },
];
const AI_ANALYTICS_TABS = [
  { key: "voice",        label: "Voice Analytics",    to: "/admin/ai/analytics/voice" },
  { key: "receptionist", label: "Receptionist KPIs",  to: "/admin/ai/analytics/receptionist" },
  { key: "briefs",       label: "AI Briefs",          to: "/admin/ai/analytics/briefs" },
  { key: "pricing",      label: "AI Pricing",         to: "/admin/ai/analytics/pricing" },
];
const FINANCE_TABS = [
  { key: "invoices",            label: "Invoices",            to: "/admin/finance/invoices" },
  { key: "settlements",         label: "Settlements",         to: "/admin/finance/settlements" },
  { key: "payroll",             label: "Payroll",             to: "/admin/finance/payroll" },
  { key: "payment-preferences", label: "Payment Preferences", to: "/admin/finance/payment-preferences" },
  { key: "profitability",       label: "Profitability",       to: "/admin/finance/profitability" },
  { key: "factoring",           label: "Factoring",           to: "/admin/finance/factoring" },
];
const SETTINGS_TABS = [
  { key: "integrations",      label: "Integrations",          to: "/admin/settings-hub/integrations" },
  { key: "edi",               label: "EDI",                   to: "/admin/settings-hub/edi" },
  { key: "users",             label: "Users",                 to: "/admin/settings-hub/users" },
  { key: "general",           label: "General",               to: "/admin/settings-hub/general" },
  { key: "branding",          label: "Branding",              to: "/admin/settings-hub/branding" },
  { key: "data",              label: "Data Management",       to: "/admin/settings-hub/data" },
  { key: "events",            label: "Activity Log",          to: "/admin/settings-hub/events" },
  { key: "onboarding-config", label: "Launch Wizard",         to: "/admin/settings-hub/onboarding-config" },
  { key: "readiness",         label: "Deployment Readiness",  to: "/admin/settings-hub/readiness" },
];

// Customer portal
const CustomerHome = lazy(() => import("./pages/customer/CustomerHome"));
const CustomerLoads = lazy(() => import("./pages/customer/CustomerLoads"));
const CustomerLoadDetail = lazy(() => import("./pages/customer/CustomerLoadDetail"));
const CustomerInvoices = lazy(() => import("./pages/customer/CustomerInvoices"));
const CustomerInvoicePaymentSuccess = lazy(() => import("./pages/customer/CustomerInvoicePaymentSuccess"));
const CustomerNotifications = lazy(() => import("./pages/customer/CustomerNotifications"));
const CustomerProfile = lazy(() => import("./pages/customer/CustomerProfile"));
const CustomerQuoteRequest = lazy(() => import("./pages/customer/CustomerQuoteRequest"));
const BrokerShell = lazy(() => import("./pages/broker/BrokerShell"));
const BrokerHome = lazy(() => import("./pages/broker/BrokerHome"));
const BrokerLoads = lazy(() => import("./pages/broker/BrokerLoads"));
const BrokerLoadDetail = lazy(() => import("./pages/broker/BrokerLoadDetail"));
const BrokerLoadRequest = lazy(() => import("./pages/broker/BrokerLoadRequest"));
const BrokerInvoices = lazy(() => import("./pages/broker/BrokerInvoices"));
const BrokerTimeline = lazy(() => import("./pages/broker/BrokerTimeline"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const CustomerTrack = lazy(() => import("./pages/CustomerTrack"));

// Carrier portal
const CarrierPortal = lazy(() => import("./pages/carrier/CarrierPortal"));
const CarrierSignup = lazy(() => import("./pages/CarrierSignup"));
const CarrierInviteAccept = lazy(() => import("./pages/carrier/CarrierInviteAccept"));

// Driver portal
const DriverHome = lazy(() => import("./pages/driver/DriverHome"));
const DriverLoad = lazy(() => import("./pages/driver/DriverLoad"));
const DriverLoadsList = lazy(() => import("./pages/driver/DriverLoadsList"));
const DriverChat = lazy(() => import("./pages/driver/DriverChat"));
const DriverNotifications = lazy(() => import("./pages/driver/DriverNotifications"));
const DriverDocs = lazy(() => import("./pages/driver/DriverDocs"));
const DriverSafety = lazy(() => import("./pages/driver/DriverSafety"));
const DriverProfile = lazy(() => import("./pages/driver/DriverProfile"));
const DriverHOS = lazy(() => import("./pages/driver/DriverHOS"));
const DriverMore = lazy(() => import("./pages/driver/DriverMore"));
const DriverSettlements = lazy(() => import("./pages/driver/DriverSettlements"));
const DriverPayments = lazy(() => import("./pages/driver/DriverPayments"));
const DriverCompany = lazy(() => import("./pages/driver/DriverCompany"));

// Public / auth
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const SmsPolicy = lazy(() => import("./pages/SmsPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Eula = lazy(() => import("./pages/Eula"));
const Support = lazy(() => import("./pages/Support"));
const ChangePasswordRequired = lazy(() => import("./pages/ChangePasswordRequired"));
const TotpChallenge = lazy(() => import("./pages/TotpChallenge"));

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (loading) return <RouteFallback label="Signing in" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && path !== "/change-password-required") {
    return <Navigate to="/change-password-required" replace />;
  }
  if (user.requires_2fa_enrollment && path !== "/admin/security") {
    return <Navigate to="/admin/security" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/**
 * AdminOnly — route-level guard for admin-restricted hubs.
 *
 * The outer <Protected roles={["admin","dispatcher"]}> lets dispatchers
 * INTO the admin shell, but a handful of hubs (Settings, AI Knowledge,
 * AI Configuration, Recruiting Pipeline / Campaigns / Analytics) are
 * admin-only. Wrap those <Route element=…> with this guard so the
 * child component never mounts for a dispatcher — they get an inline
 * AccessDenied panel with a link back to the Dashboard instead of a
 * page shell full of 401 API errors.
 */
function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <AccessDenied />;
  return children;
}

/**
 * AccessDenied — rendered inside the admin shell when a signed-in
 * user (usually a dispatcher) hits a route reserved for admins.
 * Deliberately embedded here instead of a separate file so the guard
 * + panel ship as one small unit.
 */
function AccessDenied() {
  return (
    <div className="p-6 lg:p-8" data-testid="access-denied">
      <div
        className="rf-card rf-card-dark rf-card-elevated"
        style={{ padding: 48, textAlign: "center", maxWidth: 560, margin: "60px auto" }}
      >
        <div
          className="inline-flex items-center justify-center rounded-full mb-4"
          style={{ width: 56, height: 56, background: "#E5091422", border: "1px solid #E5091455" }}
        >
          <span style={{ color: "#E50914", fontSize: 24, fontWeight: 900 }}>!</span>
        </div>
        <div className="font-display font-black text-xl text-white mb-2">
          Access denied
        </div>
        <div className="text-sm mb-5" style={{ color: "#A3A3A3", maxWidth: 420, margin: "0 auto" }}>
          This section is restricted to administrators. If you need access, ask an admin to grant you the required role.
        </div>
        <NavigateSafeLink />
      </div>
    </div>
  );
}

/**
 * Wraps <Link> to /admin/command with a stable data-testid. Kept as a
 * tiny helper so the AccessDenied panel stays declarative.
 */
function NavigateSafeLink() {
  // Using window.location instead of react-router <Link> so the guard
  // works even if the user hit the URL directly (before <Route> match).
  return (
    <a
      href="/admin/command"
      className="rf-btn rf-btn-primary text-sm"
      style={{ padding: "8px 16px" }}
      data-testid="access-denied-back-dashboard"
    >
      Return to Dashboard
    </a>
  );
}

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback label="Loading Roll Fast TMS" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password-required" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  if (user.role === "customer") return <Navigate to="/customer" replace />;
  if (user.role === "carrier") return <Navigate to="/carrier" replace />;
  if (user.role === "broker") return <Navigate to="/broker" replace />;
  return <Navigate to="/admin" replace />;
}

/**
 * Suspense fallback used for every lazy route. Renders a branded
 * skeleton instead of a jarring blank flash while the chunk downloads.
 */
function RouteFallback({ label = "Loading" }) {
  return (
    <div className="min-h-[60vh] p-6 bg-[#050505] text-white" data-testid="route-fallback">
      <LoadingSkeleton variant="page" tiles={4} rows={5} cols={5} label={label} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <PWAInstallPrompt />
        <LaunchExperience />
        {/* Keyboard-only skip-to-content link — WCAG 2.4.1 */}
        <a href="#main-content" className="rf-skip-link" data-testid="skip-to-content">
          Skip to main content
        </a>
        <ErrorBoundary area="app">
          <Suspense fallback={<RouteFallback label="Loading page" />}>
            <Routes>
              <Route path="/" element={<RoleHome />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dispatch-services" element={<RequestDispatchServices />} />
              {/* Legacy /quote route redirects to dispatch-services (Roll Fast is a dispatch service, not a broker) */}
              <Route path="/quote" element={<RequestDispatchServices />} />
              <Route path="/totp-challenge" element={<TotpChallenge />} />
              <Route path="/change-password-required" element={
                <Protected><ChangePasswordRequired /></Protected>
              } />

              <Route path="/admin" element={<Protected roles={["admin", "dispatcher"]}><ErrorBoundary area="admin"><AdminShell /></ErrorBoundary></Protected>}>
                <Route index element={<CommandCenter />} />
                <Route path="command" element={<CommandCenter />} />
                <Route path="command/legacy" element={<Navigate to="/admin" replace />} />
                <Route path="activity" element={<ActivityTimeline />} />
                <Route path="kpis" element={<OpsKPIs />} />

                {/* ═══ OPERATIONS · 4 tabbed hubs ═══════════════════ */}
                <Route path="operations/dispatch" element={<TabbedHub tabs={OPS_DISPATCH_TABS} testid="ops-dispatch-hub" />}>
                  <Route index element={<Navigate to="loads" replace />} />
                  <Route path="loads"       element={<LoadBoard />} />
                  <Route path="sourcing"    element={<LoadBoardHub />} />
                  <Route path="ai-booking"  element={<AILoadBooking />} />
                  <Route path="map"         element={<LiveMap />} />
                  <Route path="checkcalls"  element={<CheckCalls />} />
                </Route>
                <Route path="operations/drivers" element={<TabbedHub tabs={OPS_DRIVERS_TABS} testid="ops-drivers-hub" />}>
                  <Route index element={<Navigate to="drivers" replace />} />
                  <Route path="drivers"     element={<DriversPage />} />
                  <Route path="dispatchers" element={<Dispatchers />} />
                </Route>
                <Route path="operations/vehicles" element={<TabbedHub tabs={OPS_VEHICLES_TABS} testid="ops-vehicles-hub" />}>
                  <Route index element={<Navigate to="vehicles" replace />} />
                  <Route path="vehicles"  element={<Vehicles />} />
                  <Route path="carriers"  element={<Carriers />} />
                  <Route path="brokers"   element={<BrokersPage />} />
                  <Route path="referrals" element={<Referrals />} />
                </Route>
                {/* iter-139 · Carrier-centric Operations hub — first-class
                     home for the carrier directory and the unified
                     Application Review queue. Legacy /operations/vehicles/*
                     routes above are preserved for deep-link safety. */}
                <Route path="operations/carriers" element={<TabbedHub tabs={OPS_CARRIERS_TABS} testid="ops-carriers-hub" />}>
                  <Route index element={<Navigate to="directory" replace />} />
                  <Route path="directory"    element={<Carriers />} />
                  <Route path="applications" element={<UnifiedApplicationReview />} />
                  <Route path="brokers"      element={<BrokersPage />} />
                  <Route path="referrals"    element={<Referrals />} />
                </Route>
                {/* iter-139 · Per-carrier operational profile. Flat path
                     (sibling of /admin/drivers/:id) to avoid any
                     collision with the carriers hub's child tabs. */}
                <Route path="carriers/:carrierId" element={<CarrierOperationalProfile />} />
                <Route path="operations/compliance" element={<TabbedHub tabs={OPS_COMPLIANCE_TABS} testid="ops-compliance-hub" />}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview"   element={<Compliance />} />
                  <Route path="ifta"       element={<Ifta />} />
                  <Route path="audit"      element={<AuditLogs />} />
                  <Route path="security"   element={<Security />} />
                  <Route path="onboarding" element={<CarrierPipeline />} />
                </Route>

                {/* Driver detail deep links (kept flat — invoked from Drivers tab) */}
                <Route path="drivers/new"      element={<DriverForm />} />
                <Route path="drivers/:id/edit" element={<DriverForm />} />
                <Route path="drivers/:id"      element={<DriverDetail />} />
                <Route path="loads/:id"        element={<LoadDetail />} />

                {/* ═══ RECRUITING · single workspace with 5 nested hubs ═════ */}
                <Route path="recruiting" element={<AdminOnly><RecruitingWorkspace /></AdminOnly>}>
                  <Route index element={<Navigate to="pipeline" replace />} />
                  <Route path="pipeline" element={<TabbedHub tabs={REC_PIPELINE_TABS} testid="recruiting-pipeline-hub" />}>
                    <Route index element={<Navigate to="pipeline" replace />} />
                    <Route path="pipeline"  element={<RecruitingPipeline />} />
                    <Route path="discovery" element={<CarrierDiscovery />} />
                    <Route path="journey"   element={<ProspectTimeline />} />
                  </Route>
                  <Route path="campaigns" element={<TabbedHub tabs={REC_CAMPAIGNS_TABS} testid="recruiting-campaigns-hub" />}>
                    <Route index element={<Navigate to="sequences" replace />} />
                    <Route path="sequences" element={<FollowupSequences />} />
                    <Route path="needs-info" element={<CarrierFollowUpQueue />} />
                    <Route path="settings"  element={<Recruiting />} />
                  </Route>
                  <Route path="applicants" element={<TabbedHub tabs={REC_APPLICANTS_TABS} testid="recruiting-applicants-hub" />}>
                    <Route index element={<Navigate to="applications" replace />} />
                    <Route path="applications"        element={<Applications />} />
                    <Route path="voice-applications"  element={<VoiceCarrierApplications />} />
                    <Route path="leads"               element={<Leads />} />
                  </Route>
                  <Route path="analytics" element={<TabbedHub tabs={REC_ANALYTICS_TABS} testid="recruiting-analytics-hub" />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview"    element={<RecruitingAnalytics />} />
                    <Route path="recruiter"   element={<RecruiterAnalytics />} />
                    <Route path="follow-up"   element={<FollowUpAgent />} />
                    <Route path="scoring"     element={<ScoringWeights />} />
                  </Route>
                  {/* iter-127 · Recruiting Operations Center (health + forensics + reset + flags) */}
                  <Route path="operations" element={<RecruitingOps />} />
                </Route>
                {/* iter-131 · Voice Assignments — now a tab inside Voice Engine; legacy standalone URL redirects */}
                <Route path="voice/assignments" element={<Navigate to="/admin/ai/configuration/assignments" replace />} />
                <Route path="carrier-profile/:prospectId" element={<CarrierProfile />} />

                {/* ═══ AI CENTER · 5 tabbed hubs ════════════════════ */}
                <Route path="ai/agents" element={<TabbedHub tabs={AI_AGENTS_TABS} testid="ai-agents-hub" />}>
                  <Route index element={<Navigate to="control" replace />} />
                  <Route path="control"    element={<AIControl />} />
                  <Route path="autonomous" element={<AutonomousOps />} />
                  <Route path="personas"   element={<VoicePersonas />} />
                  <Route path="reminders"  element={<FollowUps />} />
                </Route>
                <Route path="ai/calls" element={<TabbedHub tabs={AI_CALLS_TABS} testid="ai-calls-hub" />}>
                  <Route index element={<Navigate to="live" replace />} />
                  <Route path="live"      element={<VoiceLive />} />
                  <Route path="history"   element={<VoiceHistory />} />
                  <Route path="actions"   element={<VoiceActions />} />
                  <Route path="transfers" element={<Navigate to="/admin/ai/configuration/transfers" replace />} />
                </Route>
                <Route path="ai/analytics" element={<TabbedHub tabs={AI_ANALYTICS_TABS} testid="ai-analytics-hub" />}>
                  <Route index element={<Navigate to="voice" replace />} />
                  <Route path="voice"        element={<VoiceAnalytics />} />
                  <Route path="receptionist" element={<ReceptionistAnalytics />} />
                  <Route path="briefs"       element={<BriefsTimeline />} />
                  <Route path="pricing"      element={<Pricing />} />
                </Route>
                <Route path="ai/knowledge" element={<AdminOnly><Knowledge /></AdminOnly>} />
                <Route path="ai/configuration" element={<AdminOnly><VoiceEngine /></AdminOnly>}>
                  <Route index element={<Navigate to="voice" replace />} />
                  <Route path="voice"       element={<VoiceConfig />} />
                  <Route path="provider"    element={<TTSSettings />} />
                  <Route path="assignments" element={<VoiceAssignments />} />
                  <Route path="transfers"   element={<VoiceTransferTargets />} />
                  <Route path="alerts"      element={<VoiceMonitor />} />
                  <Route path="health"      element={<VoiceHealth />} />
                </Route>

                {/* ═══ FINANCE · single hub with 6 tabs ═════════════ */}
                <Route path="finance" element={<TabbedHub tabs={FINANCE_TABS} testid="finance-hub" />}>
                  <Route index element={<Navigate to="invoices" replace />} />
                  <Route path="invoices"             element={<Invoices />} />
                  <Route path="settlements"          element={<SettlementsPage />} />
                  <Route path="payroll"              element={<Payroll />} />
                  <Route path="payment-preferences"  element={<PaymentPreferencesAdmin />} />
                  <Route path="profitability"        element={<Profitability />} />
                  <Route path="factoring"            element={<Factoring />} />
                </Route>

                {/* ═══ SETTINGS · single hub with 10 tabs ═══════════ */}
                <Route path="settings-hub" element={<AdminOnly><TabbedHub tabs={SETTINGS_TABS} testid="settings-hub" /></AdminOnly>}>
                  <Route index element={<Navigate to="integrations" replace />} />
                  <Route path="integrations" element={<IntegrationsHub />}>
                    <Route index element={<Navigate to="api-keys" replace />} />
                    <Route path="api-keys"    element={<Integrations />} />
                    <Route path="monitoring"  element={<IntegrationCenter />} />
                  </Route>
                  <Route path="api-keys"          element={<Navigate to="/admin/settings-hub/integrations/api-keys" replace />} />
                  <Route path="edi"               element={<Edi />} />
                  <Route path="users"             element={<UsersPage />} />
                  <Route path="general"           element={<AdminSettings />} />
                  <Route path="branding"          element={<Branding />} />
                  <Route path="data"              element={<DataManagement />} />
                  <Route path="events"            element={<EventsLog />} />
                  <Route path="onboarding-config" element={<OnboardingConfig />} />
                  <Route path="readiness"         element={<GoLiveReadiness />} />
                </Route>

                {/* ═══ Legacy flat-URL redirects (deep-link preservation) ═══ */}
                {/* Dispatch */}
                <Route path="loads"           element={<Navigate to="/admin/operations/dispatch/loads" replace />} />
                <Route path="loadboard"       element={<Navigate to="/admin/operations/dispatch/sourcing" replace />} />
                <Route path="ai-load-booking" element={<Navigate to="/admin/operations/dispatch/ai-booking" replace />} />
                <Route path="map"             element={<Navigate to="/admin/operations/dispatch/map" replace />} />
                <Route path="checkcalls"      element={<Navigate to="/admin/operations/dispatch/checkcalls" replace />} />
                {/* Drivers */}
                <Route path="drivers"         element={<Navigate to="/admin/operations/drivers/drivers" replace />} />
                <Route path="dispatchers"     element={<Navigate to="/admin/operations/drivers/dispatchers" replace />} />
                {/* Vehicles */}
                <Route path="carriers"        element={<Navigate to="/admin/operations/vehicles/carriers" replace />} />
                <Route path="brokers"         element={<Navigate to="/admin/operations/vehicles/brokers" replace />} />
                <Route path="referrals"       element={<Navigate to="/admin/operations/vehicles/referrals" replace />} />
                {/* Compliance */}
                <Route path="compliance"      element={<Navigate to="/admin/operations/compliance/overview" replace />} />
                <Route path="ifta"            element={<Navigate to="/admin/operations/compliance/ifta" replace />} />
                <Route path="audit"           element={<Navigate to="/admin/operations/compliance/audit" replace />} />
                <Route path="security"        element={<Navigate to="/admin/operations/compliance/security" replace />} />
                <Route path="carrier-pipeline" element={<Navigate to="/admin/operations/compliance/onboarding" replace />} />
                {/* Recruiting */}
                <Route path="recruiting-pipeline"    element={<Navigate to="/admin/recruiting/pipeline/pipeline" replace />} />
                <Route path="carrier-discovery"      element={<Navigate to="/admin/recruiting/pipeline/discovery" replace />} />
                <Route path="voice/prospect-timeline" element={<Navigate to="/admin/recruiting/pipeline/journey" replace />} />
                <Route path="followup-sequences"     element={<Navigate to="/admin/recruiting/campaigns/sequences" replace />} />
                <Route path="carrier-follow-up"      element={<Navigate to="/admin/recruiting/campaigns/needs-info" replace />} />
                <Route path="recruiting"             element={<Navigate to="/admin/recruiting/campaigns/settings" replace />} />
                <Route path="applications"           element={<Navigate to="/admin/recruiting/applicants/applications" replace />} />
                <Route path="voice/carrier-applications" element={<Navigate to="/admin/recruiting/applicants/voice-applications" replace />} />
                <Route path="leads"                  element={<Navigate to="/admin/recruiting/applicants/leads" replace />} />
                <Route path="recruiting-analytics"   element={<Navigate to="/admin/recruiting/analytics/overview" replace />} />
                <Route path="voice/recruiter-analytics" element={<Navigate to="/admin/recruiting/analytics/recruiter" replace />} />
                <Route path="voice/follow-up-agent"  element={<Navigate to="/admin/recruiting/analytics/follow-up" replace />} />
                <Route path="scoring-weights"        element={<Navigate to="/admin/recruiting/analytics/scoring" replace />} />
                {/* AI Center */}
                <Route path="ai"           element={<Navigate to="/admin/ai/agents/control" replace />} />
                <Route path="autonomous"   element={<Navigate to="/admin/ai/agents/autonomous" replace />} />
                <Route path="voice/prompts" element={<Navigate to="/admin/ai/agents/personas" replace />} />
                <Route path="followups"    element={<Navigate to="/admin/ai/agents/reminders" replace />} />
                <Route path="voice"        element={<Navigate to="/admin/ai/calls/live" replace />} />
                <Route path="voice/history" element={<Navigate to="/admin/ai/calls/history" replace />} />
                <Route path="voice/actions" element={<Navigate to="/admin/ai/calls/actions" replace />} />
                <Route path="voice/rules"   element={<Navigate to="/admin/ai/configuration/transfers" replace />} />
                <Route path="voice/analytics" element={<Navigate to="/admin/ai/analytics/voice" replace />} />
                <Route path="voice/receptionist-analytics" element={<Navigate to="/admin/ai/analytics/receptionist" replace />} />
                <Route path="briefs"        element={<Navigate to="/admin/ai/analytics/briefs" replace />} />
                <Route path="pricing"       element={<Navigate to="/admin/ai/analytics/pricing" replace />} />
                <Route path="knowledge"     element={<Navigate to="/admin/ai/knowledge" replace />} />
                <Route path="voice/knowledge" element={<Navigate to="/admin/ai/knowledge" replace />} />
                <Route path="voice/config"  element={<Navigate to="/admin/ai/configuration/voice" replace />} />
                <Route path="voice/tts-settings" element={<Navigate to="/admin/ai/configuration/provider" replace />} />
                <Route path="voice/monitor" element={<Navigate to="/admin/ai/configuration/alerts" replace />} />
                <Route path="voice/health"  element={<Navigate to="/admin/ai/configuration/health" replace />} />
                {/* Finance */}
                <Route path="invoices"            element={<Navigate to="/admin/finance/invoices" replace />} />
                <Route path="settlements"         element={<Navigate to="/admin/finance/settlements" replace />} />
                <Route path="payroll"             element={<Navigate to="/admin/finance/payroll" replace />} />
                <Route path="payment-preferences" element={<Navigate to="/admin/finance/payment-preferences" replace />} />
                <Route path="profitability"       element={<Navigate to="/admin/finance/profitability" replace />} />
                <Route path="factoring"           element={<Navigate to="/admin/finance/factoring" replace />} />
                {/* Settings */}
                <Route path="integration-center"  element={<Navigate to="/admin/settings-hub/integrations/monitoring" replace />} />
                <Route path="integrations"        element={<Navigate to="/admin/settings-hub/integrations/api-keys" replace />} />
                <Route path="edi"                 element={<Navigate to="/admin/settings-hub/edi" replace />} />
                <Route path="users"               element={<Navigate to="/admin/settings-hub/users" replace />} />
                <Route path="settings"            element={<Navigate to="/admin/settings-hub/general" replace />} />
                <Route path="branding"            element={<Navigate to="/admin/settings-hub/branding" replace />} />
                <Route path="data"                element={<Navigate to="/admin/settings-hub/data" replace />} />
                <Route path="events"              element={<Navigate to="/admin/settings-hub/events" replace />} />
                <Route path="onboarding-config"   element={<Navigate to="/admin/settings-hub/onboarding-config" replace />} />
                <Route path="readiness"           element={<Navigate to="/admin/settings-hub/readiness" replace />} />

                <Route path="*" element={<AdminNotFound />} />
              </Route>

              <Route path="/driver" element={<Protected roles={["driver"]}><ErrorBoundary area="driver"><DriverShell /></ErrorBoundary></Protected>}>
                <Route index element={<DriverHome />} />
                <Route path="loads" element={<DriverLoadsList />} />
                <Route path="loads/:id" element={<DriverLoad />} />
                <Route path="chat" element={<DriverChat />} />
                <Route path="chat/:id" element={<DriverChat />} />
                <Route path="docs" element={<DriverDocs />} />
                <Route path="notifications" element={<DriverNotifications />} />
                <Route path="safety" element={<DriverSafety />} />
                <Route path="profile" element={<DriverProfile />} />
                <Route path="hos" element={<DriverHOS />} />
                <Route path="more" element={<DriverMore />} />
                <Route path="settlements" element={<DriverSettlements />} />
                <Route path="payments" element={<DriverPayments />} />
                <Route path="company" element={<DriverCompany />} />
              </Route>

              <Route path="/customer" element={<Protected roles={["customer"]}><ErrorBoundary area="customer"><CustomerShell /></ErrorBoundary></Protected>}>
                <Route index element={<CustomerHome />} />
                <Route path="loads" element={<CustomerLoads />} />
                <Route path="loads/:id" element={<CustomerLoadDetail />} />
                <Route path="invoices" element={<CustomerInvoices />} />
                <Route path="invoices/payment-success" element={<CustomerInvoicePaymentSuccess />} />
                <Route path="notifications" element={<CustomerNotifications />} />
                <Route path="profile" element={<CustomerProfile />} />
                <Route path="quote" element={<CustomerQuoteRequest />} />
              </Route>
              <Route path="/customer-legacy" element={<Protected roles={["customer"]}><CustomerDashboard /></Protected>} />

              <Route path="/broker" element={<Protected roles={["broker"]}><ErrorBoundary area="broker"><BrokerShell /></ErrorBoundary></Protected>}>
                <Route index element={<BrokerHome />} />
                <Route path="loads" element={<BrokerLoads />} />
                <Route path="loads/:id" element={<BrokerLoadDetail />} />
                <Route path="request" element={<BrokerLoadRequest />} />
                <Route path="invoices" element={<BrokerInvoices />} />
                <Route path="timeline" element={<BrokerTimeline />} />
              </Route>

              <Route path="/track/:token" element={<CustomerTrack />} />
              <Route path="/carrier" element={<Protected roles={["carrier"]}><ErrorBoundary area="carrier"><CarrierPortal /></ErrorBoundary></Protected>} />
              <Route path="/carrier/signup" element={<CarrierSignup />} />
              <Route path="/carrier/team/accept" element={<CarrierInviteAccept />} />
              <Route path="/carrier/driver/accept" element={<CarrierInviteAccept />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/sms-policy" element={<SmsPolicy />} />
              <Route path="/terms" element={<SmsPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/eula" element={<Eula />} />
              <Route path="/support" element={<Support />} />
              <Route path="/contact" element={<Support />} />
              <Route path="/help" element={<Support />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}
